import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { flushSync } from 'react-dom';
import { retrieveUserFiles, retrievePatientFiles, uploadFile, uploadLabImages, deleteFile } from '../components/API';
import { useUserFiles } from '../context/UserFilesProvider';
import { useFilters } from '../context/FilterProvider';
import { filterAndSortBloodTests, isBloodTest, processUploadedFile } from '../util/GenericUtil';
import { groupFilesByDraw } from '../util/DrawGroupUtil';
import {
    reconcileMerges, mergeDrawGroup, mergedFileName,
    autoMergeDecision, autoMergeSignature,
} from '../util/labMerge';
import { cacheFiles } from '../util/FileCacheUtil';
import { validateFile } from '../util/UploadValidation';
import { convertCsvToJson } from '../util/UploadUtil';
import { IMAGE_EXTENSIONS, isImageFile, planBatches, prepareImageForUpload } from '../util/imagePrep';

// How long the file list must stop changing before a draw is collapsed on its
// own. Selecting seven panel sheets fires seven concurrent uploads, each landing
// in userFiles separately; merging on the first arrival would rewrite the draw
// six more times and hammer the API. Every change restarts this clock, so the
// merge happens once, after the burst.
const AUTO_MERGE_QUIET_MS = 4000;

export function useBloodTestFiles({
    user, patientId = null, normalizeTestName,
    // Both view flags, deliberately, rather than one pre-combined boolean. The
    // caller had to decide which of doctorViewMode/readOnly meant "cannot write"
    // and got it wrong, switching the automatic merge off for the doctor session
    // it was being tested from. The policy belongs here, once.
    doctorViewMode = false, readOnly = false,
    // Whether normalizeTestName can actually resolve a canonical name yet. It is
    // callable from the first render but returns its input until the name map
    // finishes loading, and a merge that runs in that window bakes raw lab names
    // into the stored document.
    namesReady = true,
}) {
    // A doctor working a patient uploads and deletes on that patient's behalf, so
    // their session does this housekeeping like any other. Only a view-only peer
    // must not write. doctorViewMode is intentionally unused for that decision.
    const cannotWrite = readOnly;
    const { userFiles, setUserFiles } = useUserFiles();
    const { selectFile } = useFilters();
    const [rawBloodTestFiles, setRawBloodTestFiles] = useState(undefined);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [uploadingState, setUploadingState] = useState(false);
    const [uploadProgresses, setUploadProgresses] = useState([]);

    const refreshUserFiles = useCallback(async (forceRefresh = false) => {
        selectFile("all");
        const res = patientId
            ? await retrievePatientFiles(patientId, setLoadingFiles, normalizeTestName, forceRefresh)
            : await retrieveUserFiles(setLoadingFiles, normalizeTestName, forceRefresh);
        setUserFiles(res);
    }, [patientId, normalizeTestName, selectFile, setUserFiles]);

    // Load user's own files on auth
    useEffect(() => {
        if (!patientId && user && !userFiles) {
            refreshUserFiles();
        }
    }, [user, patientId]);

    // Extract and sort blood tests when userFiles changes
    useEffect(() => {
        setRawBloodTestFiles(filterAndSortBloodTests(userFiles));
    }, [userFiles]);

    // Recover a merge that was interrupted between uploading the merged document
    // and deleting its sources. Hiding the superseded sources is the correctness
    // step, not the delete: while both copies are present every aggregate view
    // (allTests flattens all bodies) would count the same marker twice. Doing it
    // here means the view is right the moment files load, whether or not the
    // cleanup deletes below ever succeed.
    const reconciled = useMemo(
        () => (rawBloodTestFiles === undefined ? undefined : reconcileMerges(rawBloodTestFiles, normalizeTestName)),
        [rawBloodTestFiles, normalizeTestName]
    );

    // Collapse files belonging to the same draw into one entry. Panel-per-sheet
    // exports (Quest via Epic) arrive as several files sharing a testdate; the UI
    // keys tests by file, so without this each panel would render its own pill.
    const bloodTestFiles = useMemo(
        () => (reconciled === undefined ? undefined : groupFilesByDraw(reconciled.visible, normalizeTestName)),
        [reconciled, normalizeTestName]
    );

    // Housekeeping: drop the sources a merged document has already absorbed.
    // Attempted keys are remembered so a rejected delete (a view-only peer gets a
    // 403) is not retried on every render. Failure is survivable — the files stay
    // hidden either way and the next session tries again.
    const cleanedUpKeys = useRef(new Set());
    useEffect(() => {
        const stale = (reconciled?.staleSources || []).filter(k => !cleanedUpKeys.current.has(k));
        if (stale.length === 0) return;

        let cancelled = false;
        (async () => {
            for (const key of stale) {
                cleanedUpKeys.current.add(key);
                try {
                    await deleteFile(key.split('/').pop(), patientId);
                } catch (err) {
                    console.warn(`[labMerge] cleanup of ${key} failed (non-fatal):`, err?.message || err);
                }
            }
            if (cancelled) return;
            setUserFiles(prev => (Array.isArray(prev) ? prev.filter(f => !stale.includes(f.key)) : prev));
        })();

        return () => { cancelled = true; };
    }, [reconciled, patientId, setUserFiles]);

    if (reconciled?.rejectedMerges?.length) {
        // A merged document that does not contain everything it claims to replace
        // is the file we distrust — never the sources it failed to absorb.
        console.warn(`[labMerge] ignoring incomplete merged file(s): ${reconciled.rejectedMerges.join(', ')}`);
    }

    /**
     * Run one upload request behind its own progress toast and fold the draws it
     * returns into the file list. `send` performs the request and resolves to the
     * upload-labtest / upload response.
     */
    const trackUpload = useCallback(async (displayName, send) => {
        setUploadingState(true);
        const uploadId = Date.now() + Math.random(); // unique per file even if selected simultaneously
        setUploadProgresses(prev => [...prev, {
            id: uploadId, status: 'uploading', startTime: Date.now(), fileName: displayName,
        }]);
        try {
            const uploadResponse = await send();

            // A trend export (one result column per draw date) or a screenshot
            // batch is stored as one document per draw, returned in `files`.
            // Everything else returns a single { key, data } at the top level.
            const uploadedFiles = uploadResponse?.files?.length
                ? uploadResponse.files
                : [uploadResponse];
            const processedFileObjs = uploadedFiles.map(f => processUploadedFile(f, normalizeTestName));

            flushSync(() => {
                setUserFiles(prevFiles => {
                    const updatedFiles = [...(prevFiles || [])];

                    processedFileObjs.forEach(processedFileObj => {
                        const existingIndex = updatedFiles.findIndex(f => f.key === processedFileObj.key);
                        if (existingIndex !== -1) {
                            updatedFiles[existingIndex] = processedFileObj;
                        } else {
                            updatedFiles.push(processedFileObj);
                        }
                    });

                    const cacheId = patientId || user?.userId;
                    if (cacheId) cacheFiles(cacheId, updatedFiles);
                    return updatedFiles;
                });
            });

            selectFile("all");
            setUploadProgresses(prev => prev.map(p =>
                p.id === uploadId ? { ...p, status: 'complete' } : p
            ));
        } catch (error) {
            console.error('Upload error:', error);
            setUploadProgresses(prev => prev.map(p =>
                p.id === uploadId ? { ...p, status: 'error', message: error.message || 'Upload failed' } : p
            ));
        } finally {
            setUploadingState(false);
        }
    }, [patientId, normalizeTestName, selectFile, setUserFiles]);

    const processOneFile = useCallback(async (file) => {
        const validation = await validateFile(file);
        if (!validation.valid) {
            alert(validation.error);
            return;
        }

        const displayName = file.name.replace(/\.[^.]+$/, '');
        await trackUpload(displayName, async () => {
            let processedFile = file;
            let newFileName = file.name;

            if (isBloodTest(file)) {
                const fileParts = newFileName.split(".");
                const extension = fileParts.pop();
                newFileName = `${fileParts.join(".")}_blood.${extension}`;
            }

            if (file.name.endsWith(".csv")) {
                const processedData = await convertCsvToJson(file, normalizeTestName);
                processedFile = new File([JSON.stringify(processedData)], file.name.replace(".csv", ".json"), { type: "application/json" });
            }

            return uploadFile(processedFile, patientId);
        });
    }, [patientId, normalizeTestName, trackUpload]);

    /**
     * Upload screenshots selected together as one batch, so the model reads them
     * side by side and they land as one test per collection. A selection too big
     * for one request is split; auto-merge joins the resulting files of one draw.
     */
    const processImageBatch = useCallback(async (files) => {
        const valid = [];
        for (const file of files) {
            const validation = await validateFile(file);
            if (validation.valid) valid.push(file);
            else alert(validation.error);
        }
        if (!valid.length) return;

        let prepared;
        try {
            prepared = await Promise.all(valid.map(prepareImageForUpload));
        } catch (error) {
            alert(error.message);
            return;
        }

        const batches = planBatches(prepared);
        await Promise.all(batches.map(batch => {
            const label = batch.length === 1
                ? batch[0].name.replace(/\.[^.]+$/, '')
                : `${batch.length} screenshots`;
            return trackUpload(label, () => uploadLabImages(batch, patientId));
        }));
    }, [patientId, trackUpload]);

    /**
     * Collapse one draw's files into a single document.
     *
     * Order is the whole safety story: the merged document is uploaded FIRST,
     * because until it exists nothing authorizes deleting the sources. A failure
     * before that point loses nothing; a failure after it leaves duplicates that
     * reconcileMerges hides on the next load and cleans up behind the scenes.
     *
     * Returns without writing anything when the reducer could not decide
     * something, unless the caller has accepted the conflicts.
     */
    const mergeDraw = useCallback(async (group, options = {}) => {
        const sources = group?.sourceFiles || [];
        if (sources.length < 2) return { merged: false, reason: 'single-file draw' };

        const { acceptConflicts, background, ...mergeOptions } = options;
        // A background merge must leave no trace in the UI. setUploadingState
        // swaps the Add Test button for a spinner and disables it, which reads as
        // the app doing something on its own.
        if (!background) setUploadingState(true);
        try {
            const { data, sourceKeys, conflicts } =
                mergeDrawGroup(sources, { normalizeTestName, ...mergeOptions });

            if (conflicts.length > 0 && !acceptConflicts) {
                return { merged: false, conflicts };
            }

            const name = mergedFileName(data.header, data.body);
            const uploadResponse = await uploadFile(
                new File([JSON.stringify(data)], name, { type: 'application/json' }),
                patientId
            );
            const processedFileObj = processUploadedFile(uploadResponse, normalizeTestName);

            for (const key of sourceKeys) {
                // The merged file is named from a digest of its own body, so a
                // merge that changes nothing lands on a source's exact key and
                // overwrites it. Deleting that key here would delete the document
                // just written.
                if (key === processedFileObj.key) continue;
                // Claim the key before the request, in the same ledger the
                // reconciliation cleanup reads. Both paths delete a merged draw's
                // sources: this loop, and the cleanup effect once setUserFiles
                // below makes the merged document visible next to a source that
                // has not left the list yet. Without a shared ledger the second
                // one repeats every delete about 150ms behind the first.
                if (cleanedUpKeys.current.has(key)) continue;
                cleanedUpKeys.current.add(key);
                try {
                    await deleteFile(key.split('/').pop(), patientId);
                } catch (err) {
                    // Survivable: the source stays hidden and cleanup retries later.
                    console.warn(`[labMerge] deleting ${key} failed (non-fatal):`, err?.message || err);
                }
            }

            flushSync(() => {
                setUserFiles(prevFiles => {
                    const updatedFiles = [
                        ...(prevFiles || []).filter(f => !sourceKeys.includes(f.key)),
                        processedFileObj,
                    ];
                    const cacheId = patientId || user?.userId;
                    if (cacheId) cacheFiles(cacheId, updatedFiles);
                    return updatedFiles;
                });
            });

            // Collapsing a draw does not change how many pills there are — grouping
            // had already rendered those files as one — so the numeric indices in
            // effectiveSelectedFile do not move and there is nothing to reset. Only
            // a user-initiated merge jumps to All Tests, and only because the person
            // just asked for it; doing it in the background would yank the view off
            // whatever date they were reading.
            if (!background) selectFile('all');

            return { merged: true, key: processedFileObj.key, conflicts };
        } catch (error) {
            console.error('[labMerge] merge failed:', error);
            return { merged: false, error: error.message || 'Merge failed' };
        } finally {
            if (!background) setUploadingState(false);
        }
    }, [patientId, normalizeTestName, selectFile, setUserFiles, user?.userId]);

    // Merged documents a newer source has overtaken. A draw built around one of
    // these is being re-merged to absorb a corrected result, which is the only
    // case where the newest value may quietly win - see mergeDrawGroup.
    //
    // Joined to a string so useMemo has a primitive to compare: reconcileMerges
    // returns a fresh array every render, and depending on the array itself would
    // rebuild the Set each time, re-arming the quiet-period timer below forever.
    const outdatedKey = (reconciled?.outdatedMerges || []).join('|');
    const outdatedSet = useMemo(
        () => new Set(outdatedKey ? outdatedKey.split('|') : []),
        [outdatedKey]
    );
    const isCorrection = useCallback(
        (group) => (group?.sourceKeys || []).some(k => outdatedSet.has(k)),
        [outdatedSet]
    );

    /**
     * Collapse fragmented draws on their own, once the file list settles.
     *
     * There is no timer polling here and no user action behind it. bloodTestFiles
     * is derived state, so this effect re-runs whenever a file lands; the timeout
     * below is cancelled and re-armed by every one of those re-runs, which is how
     * a burst of uploads is detected as over rather than as seven separate events.
     *
     * One draw per pass, deliberately. Merging changes userFiles, which re-runs
     * this effect, which waits out another quiet period before the next draw. Two
     * fragmented dates therefore produce two merges spaced apart instead of a
     * concurrent pair, and at most one merge is ever in flight.
     *
     * What it will not decide on its own:
     *   - markers that disagree between files (mergeDraw returns without writing,
     *     since acceptConflicts is never passed here)
     *   - a draw mixing timed and untimed files, which is a guess about identity
     *   - anyone else's data, or a read-only view
     * Those simply stay fragmented. Nothing is visible either way: grouping
     * already renders them as a single pill.
     */
    const autoMergedDraws = useRef(new Set());
    const lastDecision = useRef(null);
    const uploadsInFlight = uploadProgresses.some(p => p.status === 'uploading');

    useEffect(() => {
        const { candidate, reason } = autoMergeDecision(bloodTestFiles, {
            namesReady,
            // Not uploadingState: each concurrent upload clears it in its own
            // finally, so the first of seven to land reports the burst finished.
            uploadsInFlight,
            readOnly: cannotWrite,
            attempted: autoMergedDraws.current,
        });

        // This is the only trace the feature has. It runs with no spinner, no
        // chip and no visible change, so without a line here "it did nothing"
        // and "it never ran" look identical from the console.
        if (reason !== lastDecision.current) {
            lastDecision.current = reason;
            console.log(`[labMerge] ${candidate ? `will combine ${reason} in ${AUTO_MERGE_QUIET_MS}ms` : `standing by — ${reason}`}`);
        }
        if (!candidate) return;

        const timer = setTimeout(async () => {
            // Claimed by its sources rather than the group key, and claimed before
            // the await so a re-render mid-merge cannot start a second one.
            const signature = autoMergeSignature(candidate);
            autoMergedDraws.current.add(signature);

            const outcome = await mergeDraw(candidate, {
                background: true,
                // A corrected report re-uploaded onto its original key resolves by
                // recency; panel sheets from one burst still raise a conflict.
                recencyOrdered: isCorrection(candidate),
            });
            if (outcome?.merged) {
                console.log(`[labMerge] combined ${candidate.sourceKeys.length} files from ${candidate.testdate} into ${outcome.key}`);
                // Release it. A completed merge deletes the sources, so the draw
                // cannot reappear on its own — and if these exact keys do come
                // back, they were uploaded again and deserve another look. Holding
                // the claim would strand a re-uploaded draw fragmented for the rest
                // of the session, which is precisely what a reset looks like.
                autoMergedDraws.current.delete(signature);
                return;
            }
            // A refusal keeps its claim: conflicts do not resolve themselves, and
            // retrying on every change would loop.
            console.warn(
                `[labMerge] left ${candidate.sourceKeys.length} files from ${candidate.testdate} unmerged:`,
                outcome?.conflicts?.length ? `${outcome.conflicts.length} marker(s) disagree` : outcome?.error
            );
        }, AUTO_MERGE_QUIET_MS);

        return () => clearTimeout(timer);
    }, [bloodTestFiles, uploadsInFlight, cannotWrite, namesReady, mergeDraw, isCorrection]);

    const handleFileInput = useCallback(async () => {
        const fileInput = document.createElement("input");
        fileInput.type = "file";
        fileInput.accept = `.pdf,.csv,.json,${IMAGE_EXTENSIONS.map(ext => `.${ext}`).join(',')}`;
        fileInput.multiple = true;

        fileInput.onchange = (e) => {
            const files = Array.from(e.target.files);
            if (!files.length) return;
            // Screenshots selected together go up as one batch; every other file
            // fires its own concurrent upload with its own progress toast.
            const images = files.filter(isImageFile);
            if (images.length) processImageBatch(images);
            files.filter(file => !isImageFile(file)).forEach(file => processOneFile(file));
        };

        fileInput.click();
    }, [processOneFile, processImageBatch]);

    const dismissUploadProgress = useCallback((id) => {
        setUploadProgresses(prev => prev.filter(p => p.id !== id));
    }, []);

    return {
        bloodTestFiles,
        rawBloodTestFiles,
        loadingFiles,
        uploadingState,
        setUploadingState,
        uploadProgresses,
        dismissUploadProgress,
        refreshUserFiles,
        handleFileInput,
        mergeDraw,
        userFiles,
        setUserFiles,
    };
}
